import React, { useState, useEffect } from 'react';
import { formatInTenantTz } from '../../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { Avatar, AvatarFallback } from '../../ui/avatar';
import { Progress } from '../../ui/progress';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../ui/dialog';
import { useBusiness } from '../../../contexts/BusinessContext';
import { useCRMAuth } from '../../../contexts/CRMAuthContext';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useToast } from '../../../hooks/use-toast';
import { getErrorMessage } from '../../../lib/errorMessage';
import { isMeaningfulResolution } from '../../../lib/solutionResolution';
import {
  Ticket,
  Clock, 
  User, 
  AlertTriangle,
  CheckCircle,
  Filter,
  Search,
  ArrowUpDown,
  MessageSquare,
  Phone,
  Mail,
  Users,
  BarChart3,
  RefreshCw,
  Settings,
  Zap
} from 'lucide-react';

// Slice C — honest SLA display helpers. Never invent a deadline: a ticket with sla_configured
// false shows "No SLA", not a fake countdown.
const formatSlaRemaining = (secs, configured) => {
  if (!configured || secs === null || secs === undefined) return 'No SLA';
  const overdue = secs <= 0;
  const a = Math.abs(secs);
  const h = Math.floor(a / 3600);
  const m = Math.floor((a % 3600) / 60);
  const txt = h > 0 ? `${h}h ${m}m` : `${m}m`;
  return overdue ? `Overdue ${txt}` : `${txt} left`;
};

const slaStatusLabel = (status) => ({
  not_configured: 'No SLA',
  active: 'On track',
  due_soon: 'Due soon',
  breached: 'Breached',
  extended: 'Extended',
  paused: 'Paused',
}[status] || (status ? status.replace(/_/g, ' ') : 'No SLA'));

const TicketManagement = () => {
  const { getBusinessConversations, getBusinessStaff, currentBusiness } = useBusiness();
  const { makeAuthenticatedRequest, user } = useCRMAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortNewestFirst, setSortNewestFirst] = useState(true);
  
  // Dialog states
  const [slaRulesOpen, setSlaRulesOpen] = useState(false);
  const [automationOpen, setAutomationOpen] = useState(false);
  const [reportsOpen, setReportsOpen] = useState(false);
  const [escalateDialogOpen, setEscalateDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [priorityDialogOpen, setPriorityDialogOpen] = useState(false);
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  // Slice C — Extend SLA modal
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendData, setExtendData] = useState({ amount: '', unit: 'hours', reason: '' });
  const [extendSubmitting, setExtendSubmitting] = useState(false);
  // Slice D — real customer context (history + satisfaction + honest tier) for the selected ticket
  const [customerContext, setCustomerContext] = useState(null);
  const [customerContextLoading, setCustomerContextLoading] = useState(false);
  // M2 — read-only account-deletion governance projection (Z8/Z13); shown only for account_deletion tickets.
  const [deletionProjection, setDeletionProjection] = useState(null);
  const [deletionProjectionLoading, setDeletionProjectionLoading] = useState(false);
  const [deletionProjectionError, setDeletionProjectionError] = useState(null);

  // Quick actions state
  const [escalateData, setEscalateData] = useState({
    manager: '',
    reason: '',
    urgency: 'normal'
  });
  const [noteData, setNoteData] = useState({
    note: '',
    visibility: 'internal'
  });
  const [priorityData, setPriorityData] = useState({
    priority: 'medium',
    reason: ''
  });
  const [resolveData, setResolveData] = useState({
    resolution: '',
    customerNotified: true,
    solutionTitle: '',
    resolutionSteps: '',   // REUSABLE resolution authored for Solution Bank review (distinct from the ticket note) — MANDATORY
  });
  
  const [slaRules, setSlaRules] = useState([]);
  const [automationRules, setAutomationRules] = useState([]);
  
  const [newSlaRule, setNewSlaRule] = useState({
    name: '',
    condition: '',
    action: '',
    priority: 'medium'
  });
  
  const [newAutomationRule, setNewAutomationRule] = useState({
    name: '',
    trigger: '',
    action: '',
    active: true
  });

  const [businessConversations, setBusinessConversations] = useState([]);
  const [businessStaff, setBusinessStaff] = useState([]);
  const [slaRulesLoaded, setSlaRulesLoaded] = useState(false);
  const [automationRulesLoaded, setAutomationRulesLoaded] = useState(false);
  const [quickResponseText, setQuickResponseText] = useState('');
  const [tickets, setTickets] = useState([]);          // canonical PRIMARY source: GET /tickets/business/{id}
  const [loadError, setLoadError] = useState(null);    // visible error on ticket-fetch failure (never a silent [])
  // Correction 3 — real-time online-operator presence for the Escalate assignee picker (webrtc /online-users).
  const [onlineIds, setOnlineIds] = useState(new Set());

  // Canonical loader: TICKETS are the primary source (GET /tickets/business/{id}); conversations
  // are fetched only to ENRICH each ticket. getBusinessConversations is async and MUST be awaited
  // (calling it sync returns a Promise → Array.isArray(Promise) is false → silent empty list). A
  // failed ticket fetch surfaces a visible error and is NEVER turned into a fake empty list.
  const loadTicketsAndConversations = React.useCallback(async () => {
    if (!currentBusiness?.brandId) return;
    setLoadError(null);
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/tickets/business/${currentBusiness.brandId}`);
      if (!resp.ok) throw new Error(`Ticket fetch failed (HTTP ${resp.status})`);
      const td = await resp.json();
      const conversations = await getBusinessConversations();   // async — awaited for enrichment
      const staff = getBusinessStaff();
      setTickets(Array.isArray(td?.tickets) ? td.tickets : []);
      setBusinessConversations(Array.isArray(conversations) ? conversations : []);
      setBusinessStaff(Array.isArray(staff) ? staff : []);
    } catch (e) {
      setLoadError(e?.message || 'Failed to load tickets');
      const staff = getBusinessStaff();
      setBusinessStaff(Array.isArray(staff) ? staff : []);
    }
  }, [currentBusiness?.brandId, getBusinessConversations, getBusinessStaff, makeAuthenticatedRequest]);

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await loadTicketsAndConversations(); })();
    return () => { cancelled = true; };
  }, [loadTicketsAndConversations]);

  // Slice D — fetch REAL customer context when a ticket is selected. Never fabricates: on any
  // failure the panel falls back to honest em-dashes (no fake previous-tickets/satisfaction/tier).
  useEffect(() => {
    const cid = selectedTicket?.customer_id;
    const biz = currentBusiness?.brandId;
    if (!cid || !biz) { setCustomerContext(null); return; }
    let cancelled = false;
    setCustomerContext(null);
    setCustomerContextLoading(true);
    (async () => {
      try {
        const resp = await makeAuthenticatedRequest(
          `/api/v1/analytics/customer/${encodeURIComponent(cid)}?business_id=${encodeURIComponent(biz)}`
        );
        if (!resp.ok) { if (!cancelled) setCustomerContext(null); return; }
        const data = await resp.json();
        if (!cancelled) setCustomerContext(data);
      } catch {
        if (!cancelled) setCustomerContext(null);
      } finally {
        if (!cancelled) setCustomerContextLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTicket?.customer_id, currentBusiness?.brandId, makeAuthenticatedRequest]);

  // M2 — when an ACCOUNT-DELETION ticket is selected, fetch the READ-ONLY Z8/Z13 governance projection.
  // Role-gated + tenant-scoped SERVER-SIDE (manager/admin only; cross-tenant 403/404). Never fabricates:
  // on any failure the panel shows an honest error and remains fail-closed for erasure.
  useEffect(() => {
    const isDel = selectedTicket && (selectedTicket.source === 'account_deletion' || selectedTicket.category === 'account_deletion');
    const tid = selectedTicket?.id;
    if (!isDel || !tid) { setDeletionProjection(null); setDeletionProjectionError(null); return; }
    let cancelled = false;
    setDeletionProjection(null);
    setDeletionProjectionError(null);
    setDeletionProjectionLoading(true);
    (async () => {
      try {
        const resp = await makeAuthenticatedRequest(`/api/v1/tickets/${encodeURIComponent(tid)}/deletion-policy-projection`);
        if (!resp.ok) {
          if (!cancelled) setDeletionProjectionError(resp.status === 403 ? 'not_authorized' : `http_${resp.status}`);
          return;
        }
        const data = await resp.json();
        if (!cancelled) setDeletionProjection(data);
      } catch {
        if (!cancelled) setDeletionProjectionError('fetch_failed');
      } finally {
        if (!cancelled) setDeletionProjectionLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTicket?.id, selectedTicket?.source, selectedTicket?.category, makeAuthenticatedRequest]);

  // Load SLA rules from backend on mount
  useEffect(() => {
    if (!currentBusiness?.brandId || slaRulesLoaded) return;
    makeAuthenticatedRequest(`/api/v1/sla/rules/${currentBusiness.brandId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.rules?.length) {
          setSlaRules(data.rules.map(r => ({ ...r, active: r.active !== false })));
        }
        setSlaRulesLoaded(true);
      })
      .catch(() => setSlaRulesLoaded(true));
  }, [currentBusiness?.brandId, slaRulesLoaded, makeAuthenticatedRequest]);

  // Load automation rules from backend on mount
  useEffect(() => {
    if (!currentBusiness?.brandId || automationRulesLoaded) return;
    makeAuthenticatedRequest(`/api/v1/automation/rules/${currentBusiness.brandId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.rules?.length) {
          setAutomationRules(data.rules.map(r => ({ ...r, active: r.active !== false })));
        }
        setAutomationRulesLoaded(true);
      })
      .catch(() => setAutomationRulesLoaded(true));
  }, [currentBusiness?.brandId, automationRulesLoaded, makeAuthenticatedRequest]);

  // Correction 3 — load the live online-operator roster from the EXISTING presence authority whenever the
  // Escalate dialog opens (fresh presence each time). Failure → empty set → picker shows "no operators
  // available" honestly (never a fabricated roster).
  useEffect(() => {
    if (!escalateDialogOpen || !currentBusiness?.brandId) return;
    let cancelled = false;
    (async () => {
      try {
        const resp = await makeAuthenticatedRequest(`/api/v1/webrtc/online-users/${currentBusiness.brandId}`);
        if (!resp.ok) { if (!cancelled) setOnlineIds(new Set()); return; }
        const data = await resp.json();
        const ids = (Array.isArray(data?.users) ? data.users : []).map(u => String(u || '').split('__')[0]);
        if (!cancelled) setOnlineIds(new Set(ids));
      } catch {
        if (!cancelled) setOnlineIds(new Set());
      }
    })();
    return () => { cancelled = true; };
  }, [escalateDialogOpen, currentBusiness?.brandId, makeAuthenticatedRequest]);

  const operators = businessStaff.filter(s => s?.role === 'operator');
  // GOV-7 WS1 Area C / Correction 3 — the Escalate assignee picker shows AVAILABLE ONLINE operators via the
  // EXISTING presence authority (webrtc_routes.py GET /webrtc/online-users/{business_id}). Eligibility =
  // same tenant (businessStaff is tenant-scoped) ∧ active account ∧ operator role ∧ currently online ∧ NOT
  // the current user (no self-escalation). `onlineIds` holds the presence roster (base uids, device suffix
  // stripped); an operator is online if any of its canonical ids appears there.
  const _baseId = (u) => String(u || '').split('__')[0];
  const isOperatorOnline = (s) => {
    if (!onlineIds || onlineIds.size === 0) return false;
    return [s.id, s.firebase_uid, s.staff_id, s.user_id].some(k => k && onlineIds.has(_baseId(k)));
  };
  // GOV-7 WS1 Area E — resolve the caller's staff id (best-effort, from the tenant roster) so the
  // "My Queue" filter can restrict to tickets assigned to the caller. Falls back to null → My Queue empty.
  const _normPhoneTM = (p) => (p || '').replace(/\D/g, '');
  const _me = businessStaff.find(s => {
    const uPhone = _normPhoneTM(user?.phoneNumber || user?.phone);
    const uEmail = (user?.email || '').toLowerCase().trim();
    return (uPhone && _normPhoneTM(s.phone) === uPhone) ||
           (uEmail && (s.email || '').toLowerCase().trim() === uEmail);
  });
  const currentStaffId = _me?.id || null;
  // Correction 3 — eligible AVAILABLE operators for escalation: online ∧ active ∧ operator ∧ not the caller.
  const availableOperators = operators.filter(
    s => s?.id && (s.status || 'active') !== 'inactive' && s.id !== currentStaffId && isOperatorOnline(s)
  );
  // GOV-7 WS1 Area E — canonical ticket.status filter (open|assigned|in_progress|resolved). "escalated"
  // is a SEPARATE dimension (a ticket may be open AND escalated) and is NEVER a lifecycle status here.
  const ticketMatchesStatusFilter = (ticket, f) => {
    const s = (ticket.status || '').toLowerCase();
    switch (f) {
      case 'active':   return !['resolved', 'closed'].includes(s);         // nonterminal
      case 'pending':  return s === 'open';                                 // awaiting first action
      case 'resolved': return ['resolved', 'closed'].includes(s);          // terminal only
      case 'my_queue': return !!currentStaffId && ticket.assignedId === currentStaffId;
      case 'all':
      default:         return true;
    }
  };
  // Assignment selector pool: eligible SAME-TENANT staff only (businessStaff is already tenant-scoped
  // by the backend). Excludes non-staff/customer and the platform super_admin plane. Shown name+role.
  const eligibleAssignees = businessStaff.filter(
    s => s?.id && s?.role && s.role !== 'customer' && s.role !== 'super_admin'
  );
  // Localized "Unassigned" fallback (resolved outside the map below — inside .map(t => …) the name
  // `t` is the ticket and shadows the translation fn, so we cannot call t() there).
  const unassignedLabel = t('fin.tm.unassigned');
  const roleLabel = (r) => (r ? r.charAt(0).toUpperCase() + r.slice(1) : '');

  // Ticket-primary: one row per REAL ticket, enriched from its linked conversation when available.
  // Conversations without a ticket are excluded; tickets without a conversation are preserved.
  // feedback_category is optional (null for old tickets — never suppress them).
  const _convById = {};
  businessConversations.forEach(c => { if (c && c.id) _convById[c.id] = c; });
  const enhancedTickets = tickets.map(t => {
    const conv = (t.conversation_id && _convById[t.conversation_id]) || _convById[t.id] || {};
    return {
      ...conv,                                   // enrichment ONLY (messages, customer info)
      ...t,                                      // canonical ticket fields take precedence
      id: t.ticket_id || t.id,                   // real TKT- id drives /tickets/{id}/* actions + display
      ticket_id: t.ticket_id || t.id,
      conversation_id: t.conversation_id || conv.id || null,   // real conv id drives message loading
      customer_name: t.customer_name || conv.customer_name,
      customerName: t.customer_name || conv.customerName || conv.customer_name,
      // Display the resolved STAFF NAME (backend now always sends assigned_to_name; never a raw id).
      // Unassigned tickets show the localized "Unassigned" — never a bare UUID.
      assignedTo: t.assigned_to_name || unassignedLabel,
      assignedId: t.assigned_to || '',
      status: t.status || conv.status,
      priority: t.priority || 'medium',
      feedback_category: t.feedback_category || null,
      // Slice C — REAL SLA from backend (GET /tickets/business/{id} now computes it honestly).
      // Unconfigured tickets are 'not_configured' → shown as "No SLA" (no fake 'on_track').
      slaStatus: t.sla_status || 'not_configured',
      slaDeadline: t.sla_deadline || null,
      slaConfigured: t.sla_configured === true,
      slaRemainingSeconds: (typeof t.sla_remaining_seconds === 'number') ? t.sla_remaining_seconds : null,
      timeRemaining: formatSlaRemaining(
        (typeof t.sla_remaining_seconds === 'number') ? t.sla_remaining_seconds : null,
        t.sla_configured === true,
      ),
      escalationHistory: [],
      automationTriggers: [],
      tags: [],
      source: t.source || t.category || conv.category || 'chat',
      // Slice D — real customer context is fetched per selected ticket (see customerContext state);
      // no static placeholder customerInfo here (removes fake previousTickets/satisfaction/tier).
    };
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSlaStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'due_soon': return 'bg-yellow-100 text-yellow-800';
      case 'breached': return 'bg-red-100 text-red-800';
      case 'extended': return 'bg-blue-100 text-blue-800';
      case 'paused': return 'bg-gray-100 text-gray-600';
      case 'not_configured': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'voice': return <Phone className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      case 'chat': return <MessageSquare className="h-4 w-4" />;
      default: return <MessageSquare className="h-4 w-4" />;
    }
  };

  const handleTicketAssignment = async (ticketId, staffId) => {
    if (!staffId) return;
    try {
      // Canonical assignment: POST /tickets/{id}/assign submits the canonical staff id as
      // `assigned_to`. The backend validates SAME-TENANT membership, persists assigned_to_name +
      // status, syncs the linked escalation and writes the assignment audit event to the thread.
      const resp = await makeAuthenticatedRequest(`/api/v1/tickets/${ticketId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assigned_to: staffId }),
      });
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        const assignedName = data?.ticket?.assigned_to_name || '';
        toast({ title: t('fin.tm.t.assigned'), description: t('fin.tm.t.assignedDesc', { ticket: ticketId, staff: assignedName || staffId }) });
        // Reflect the new assignee in the open detail panel immediately (name, not id).
        setSelectedTicket(prev => (prev && prev.id === ticketId)
          ? { ...prev, assignedId: staffId, assignedTo: assignedName || prev.assignedTo, status: data?.ticket?.status || prev.status }
          : prev);
        // Refresh so the assigned ticket leaves the unassigned view immediately, and tell the
        // sidebar to recompute its active-ticket metric.
        await loadTicketsAndConversations();
        window.dispatchEvent(new CustomEvent('tickets:changed'));
      } else {
        const err = await resp.json().catch(() => ({}));
        toast({ title: t('fin.tm.t.assignmentFailed'), description: getErrorMessage({ data: err, status: resp.status }, `HTTP ${resp.status}`), variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: t('fin.tm.t.assignmentFailed'), description: e?.message || t('fin.tm.t.networkError'), variant: 'destructive' });
    }
  };

  // Slice C — real SLA extension via POST /tickets/{id}/sla (management roles; backend 403s others).
  const openExtendSla = () => {
    setExtendData({ amount: '', unit: 'hours', reason: '' });
    setExtendOpen(true);
  };

  const submitExtendSla = async () => {
    if (!selectedTicket?.id) return;
    const amt = parseFloat(extendData.amount);
    if (!amt || amt <= 0) {
      toast({ title: t('fin.tm.t.invalidDuration'), description: t('fin.tm.t.enterPositive'), variant: 'destructive' });
      return;
    }
    if (!extendData.reason.trim()) {
      toast({ title: t('fin.tm.t.reasonRequired'), description: t('fin.tm.t.provideReasonExt'), variant: 'destructive' });
      return;
    }
    setExtendSubmitting(true);
    try {
      const body = extendData.unit === 'hours'
        ? { extension_hours: amt, reason: extendData.reason.trim() }
        : { extension_minutes: amt, reason: extendData.reason.trim() };
      const resp = await makeAuthenticatedRequest(`/api/v1/tickets/${selectedTicket.id}/sla`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        // 403 → honest "not permitted" message for operator/analyst/IT
        throw new Error(err.detail || `HTTP ${resp.status}`);
      }
      const sla = await resp.json();
      toast({ title: t('fin.tm.t.slaExtended'), description: t('fin.tm.t.slaExtendedDesc', { deadline: sla.sla_deadline ? new Date(sla.sla_deadline).toLocaleString() : t('fin.tm.t.updated') }) });
      setExtendOpen(false);
      // Sync the open detail panel immediately, then refresh the list (recomputes SLA).
      setSelectedTicket(prev => prev ? {
        ...prev,
        slaStatus: sla.sla_status,
        slaDeadline: sla.sla_deadline,
        slaConfigured: sla.sla_configured === true,
        slaRemainingSeconds: (typeof sla.sla_remaining_seconds === 'number') ? sla.sla_remaining_seconds : null,
        timeRemaining: formatSlaRemaining(
          (typeof sla.sla_remaining_seconds === 'number') ? sla.sla_remaining_seconds : null,
          sla.sla_configured === true,
        ),
      } : prev);
      await loadTicketsAndConversations();
    } catch (e) {
      toast({ title: t('fin.tm.t.extendFailed'), description: e?.message || t('fin.tm.t.networkError'), variant: 'destructive' });
    } finally {
      setExtendSubmitting(false);
    }
  };

  // SLA Rules Handlers
  const handleAddSlaRule = async () => {
    if (!newSlaRule.name || !newSlaRule.condition || !newSlaRule.action) {
      toast({ title: t('fin.tm.t.missingInfo'), description: t('fin.tm.t.fillRequired'), variant: "destructive" });
      return;
    }
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/sla/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newSlaRule, business_id: currentBusiness?.brandId, active: true }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setSlaRules(prev => [...prev, data.rule]);
      setNewSlaRule({ name: '', condition: '', action: '', priority: 'medium' });
      toast({ title: t('fin.tm.t.slaRuleSaved'), description: t('fin.tm.t.ruleSavedDesc', { name: data.rule.name }) });
    } catch (e) {
      toast({ title: t('fin.tm.t.saveFailed'), description: e?.message || t('fin.tm.t.networkError'), variant: "destructive" });
    }
  };

  const toggleSlaRule = async (ruleId) => {
    const rule = slaRules.find(r => r.id === ruleId);
    if (!rule) return;
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/sla/rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !rule.active }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setSlaRules(prev => prev.map(r => r.id === ruleId ? { ...r, active: !r.active } : r));
    } catch (e) {
      toast({ title: t('fin.tm.t.updateFailed'), description: e?.message, variant: "destructive" });
    }
  };

  const handleAddAutomationRule = async () => {
    if (!newAutomationRule.name || !newAutomationRule.trigger || !newAutomationRule.action) {
      toast({ title: t('fin.tm.t.missingInfo'), description: t('fin.tm.t.fillRequired'), variant: "destructive" });
      return;
    }
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/automation/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...newAutomationRule, business_id: currentBusiness?.brandId }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setAutomationRules(prev => [...prev, data.rule]);
      setNewAutomationRule({ name: '', trigger: '', action: '', active: true });
      toast({ title: t('fin.tm.t.automationRuleSaved'), description: t('fin.tm.t.ruleSavedDesc', { name: data.rule.name }) });
    } catch (e) {
      toast({ title: t('fin.tm.t.saveFailed'), description: e?.message || t('fin.tm.t.networkError'), variant: "destructive" });
    }
  };

  const toggleAutomationRule = async (ruleId) => {
    const rule = automationRules.find(r => r.id === ruleId);
    if (!rule) return;
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/automation/rules/${ruleId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ active: !rule.active }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setAutomationRules(prev => prev.map(r => r.id === ruleId ? { ...r, active: !r.active } : r));
    } catch (e) {
      toast({ title: t('fin.tm.t.updateFailed'), description: e?.message, variant: "destructive" });
    }
  };

  const handleGenerateReports = async (reportType) => {
    if (!currentBusiness?.brandId) return;
    try {
      const resp = await makeAuthenticatedRequest(
        `/api/v1/analytics/business/${currentBusiness.brandId}?period=30d`
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const lines = [
        `${reportType} Report — ${formatInTenantTz(new Date(), { dateStyle: 'medium' })}`,
        `Business: ${currentBusiness?.brandId}`,
        `Total Tickets: ${data.total_tickets ?? 0}`,
        `Resolved: ${data.resolved_tickets ?? 0}`,
        `Escalated: ${data.escalated_tickets ?? 0}`,
        `Resolution Rate: ${data.resolution_rate ?? 0}%`,
      ];
      const blob = new Blob([lines.join('\n')], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportType.toLowerCase().replace(/\s+/g, '_')}_${Date.now()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: t('fin.tm.t.reportDownloaded'), description: t('fin.tm.t.reportDownloadedDesc', { report: reportType }) });
    } catch (e) {
      toast({ title: t('fin.tm.t.reportFailed'), description: e?.message || t('fin.tm.t.networkError'), variant: "destructive" });
    }
  };
  // Quick Actions handlers
  const handleEscalateToManager = async () => {
    if (!escalateData.reason.trim()) {
      toast({ title: t('fin.tm.t.reasonRequired2'), description: t('fin.tm.t.provideEscReason'), variant: "destructive" });
      return;
    }
    // GOV-7 WS1 Area C — a governed escalation is ASSIGNED to a chosen available operator so it reaches
    // that operator's My Escalations (the backend creates a status="assigned" escalation). An assignee is
    // required; the picker holds a canonical operator staff id (never a role or display name).
    if (!escalateData.manager) {
      toast({ title: t('fin.tm.assigneeRequired'), description: t('fin.tm.selectOperatorPh'), variant: "destructive" });
      return;
    }
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/tickets/${selectedTicket.id}/escalate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason: escalateData.reason,
          // DR-012 contract: target_role is a ROLE level; the chosen assignee (a staff id) is the SEPARATE
          // canonical `assigned_to`. Escalation routed to the selected operator.
          priority: escalateData.urgency === 'critical' ? 'high' : 'medium',
          target_role: 'operator',
          assigned_to: escalateData.manager,
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      toast({ title: t('fin.tm.t.escalated'), description: t('fin.tm.t.escalatedDesc', { id: data.ticket_reference || (data.escalation_id ? 'ESC-' + String(data.escalation_id).slice(-6).toUpperCase() : '') }) });
      setEscalateData({ manager: '', reason: '', urgency: 'normal' });
      setEscalateDialogOpen(false);
      await loadTicketsAndConversations();
      window.dispatchEvent(new CustomEvent('tickets:changed'));
    } catch (err) {
      toast({ title: t('fin.tm.t.escalationFailed'), description: err?.message || t('fin.tm.t.failedEscalate'), variant: "destructive" });
    }
  };

  const handleAddInternalNote = async () => {
    if (!noteData.note.trim()) {
      toast({ title: t('fin.tm.t.noteRequired'), description: t('fin.tm.t.enterNote'), variant: "destructive" });
      return;
    }
    // enhancedTickets sets `conversation_id: t.conversation_id || conv.id || null` — so it is
    // legitimately NULL for a ticket with no linked conversation (38 of 416 on staging). Posting
    // that null made the backend lookup miss and answer 404, which is the production symptom
    // reported for T10. Quick Response already resolved this correctly; the same canonical
    // fallback is used here so the two cannot diverge again. POST /conversations/message accepts
    // either a conversation id or a ticket id — it retries the lookup on `ticket_id`.
    const convId = selectedTicket?.conversation_id || selectedTicket?.id;
    const isInternal = noteData.visibility === 'internal';
    const messageText = isInternal ? `[Internal Note] ${noteData.note}` : noteData.note;
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/conversations/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: convId,
          sender_id: currentBusiness?.brandId || 'staff',
          sender_type: 'operator',
          message: messageText,
          message_type: isInternal ? 'internal' : 'operator',
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast({ title: t('fin.tm.t.noteAdded'), description: t('fin.tm.t.noteAddedDesc', { conv: convId }) });
      setNoteData({ note: '', visibility: 'internal' });
      setNoteDialogOpen(false);
    } catch (err) {
      toast({ title: t('fin.tm.t.failedAddNote'), description: err?.message || t('fin.tm.t.failedSaveNote'), variant: "destructive" });
    }
  };

  const handleSetPriority = async () => {
    if (!selectedTicket?.id) return;
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/tickets/${selectedTicket.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priority: priorityData.priority }),
      });
      if (resp.ok) {
        toast({ title: t('fin.tm.t.priorityUpdated'), description: t('fin.tm.t.priorityUpdatedDesc', { ticket: selectedTicket.id, priority: priorityData.priority }) });
        setPriorityData({ priority: 'medium', reason: '' });
        setPriorityDialogOpen(false);
      } else {
        const err = await resp.json().catch(() => ({}));
        toast({ title: t('fin.tm.t.priorityUpdateFailed'), description: getErrorMessage({ data: err, status: resp.status }, `HTTP ${resp.status}`), variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: t('fin.tm.t.priorityUpdateFailed'), description: e?.message || t('fin.tm.t.networkError'), variant: 'destructive' });
    }
  };

  const handleMarkAsResolved = async () => {
    if (!resolveData.resolution.trim()) {
      toast({ title: t('fin.tm.t.resolutionRequired'), description: t('fin.tm.t.provideResolution'), variant: "destructive" });
      return;
    }
    // GOV-7 WS1 Area B / Correction 2 — Mark Resolved is MANDATORY-GOVERNED (no silent finalize, no opt-out
    // escape hatch): meaningful Resolution Steps are REQUIRED. No existing canonical rule distinguishes a
    // knowledge-eligible support resolution from a non-knowledge terminal event, so submission is mandatory
    // for an operator-resolved support ticket. The ONE gate (isMeaningfulResolution) blocks blank/placeholder/
    // scheduling/handoff/status-only text UP FRONT — the ticket is NOT resolved and the operator sees why.
    if (!isMeaningfulResolution(resolveData.resolutionSteps)) {
      toast({
        title: t('fin.tm.t.resolutionRequired'),
        description: t('fin.tm.resolutionStepsRequired'),
        variant: "destructive",
      });
      return;
    }
    try {
      // The reusable candidate is created by the backend BEFORE the ticket is marked resolved (governed
      // transaction). A non-reusable resolution → backend HTTP 422 → the ticket is NOT finalized and we show
      // the reason. source=human_resolution + conversation/ticket/resolve-event linkage is stamped server-side.
      const resp = await makeAuthenticatedRequest(`/api/v1/tickets/${selectedTicket.id}/resolve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resolution: resolveData.resolution,
          submit_to_solution_bank: true,
          solution_title: (resolveData.solutionTitle.trim() || selectedTicket?.subject || selectedTicket?.intent || 'Resolved issue').slice(0, 80),
          solution_resolution: resolveData.resolutionSteps.trim(),
          source_channel: 'human_chat',
        }),
      });
      if (!resp.ok) {
        // Governed transaction blocked (e.g. 422 from the quality gate): the ticket is NOT resolved.
        const err = await resp.json().catch(() => ({}));
        const detail = err && err.detail;
        const msg = typeof detail === 'string'
          ? detail
          : (detail?.message
             || (Array.isArray(detail?.validation_errors) ? detail.validation_errors.join(' ') : `HTTP ${resp.status}`));
        throw new Error(msg);
      }
      const data = await resp.json().catch(() => ({}));
      const solutionNote = data?.solution_id ? ' ' + t('fin.tm.solutionSubmittedNote') : '';
      toast({ title: t('fin.tm.t.ticketResolved'), description: `${t('fin.tm.t.ticketResolvedDesc', { ticket: selectedTicket.id })}${solutionNote}` });
      setResolveData({ resolution: '', customerNotified: true, solutionTitle: '', resolutionSteps: '' });
      setResolveDialogOpen(false);
      await loadTicketsAndConversations();
      window.dispatchEvent(new CustomEvent('tickets:changed'));
    } catch (err) {
      toast({ title: t('fin.tm.t.resolutionFailed'), description: err?.message || t('fin.tm.t.failedResolve'), variant: "destructive" });
    }
  };

  const handleReopenTicket = async (reason = '') => {
    if (!selectedTicket?.id) return;
    try {
      const resp = await makeAuthenticatedRequest(`/api/v1/tickets/${selectedTicket.id}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast({ title: t('fin.tm.t.ticketReopened'), description: t('fin.tm.t.ticketReopenedDesc', { ticket: selectedTicket.id }) });
      await loadTicketsAndConversations();
      window.dispatchEvent(new CustomEvent('tickets:changed'));
    } catch (err) {
      toast({ title: t('fin.tm.t.reopenFailed'), description: err?.message || t('fin.tm.t.failedReopen'), variant: 'destructive' });
    }
  };

  const handleQuickResponse = async (text) => {
    if (!text?.trim()) return;
    const convId = selectedTicket?.conversation_id || selectedTicket?.id;
    if (!convId) {
      toast({ title: t('fin.tm.t.noConversation'), description: t('fin.tm.t.cannotSend'), variant: "destructive" });
      return;
    }
    try {
      const resp = await makeAuthenticatedRequest('/api/v1/conversations/message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversation_id: convId,
          sender_id: currentBusiness?.brandId || 'staff',
          sender_type: 'operator',
          message: text,
          message_type: 'operator',
        }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast({ title: t('fin.tm.t.messageSent'), description: t('fin.tm.t.messageSentDesc') });
      setQuickResponseText('');
    } catch (err) {
      toast({ title: t('fin.tm.t.sendFailed'), description: err?.message || t('fin.tm.t.failedSend'), variant: "destructive" });
    }
  };

  // GOV-7 WS1 Area E — tab/filter counts derive from the SAME authority as the list (enhancedTickets,
  // i.e. GET /tickets/business/{id}) using the SAME predicate → guaranteed count↔list parity.
  const statusCounts = {
    all:      enhancedTickets.length,
    active:   enhancedTickets.filter(tk => ticketMatchesStatusFilter(tk, 'active')).length,
    pending:  enhancedTickets.filter(tk => ticketMatchesStatusFilter(tk, 'pending')).length,
    resolved: enhancedTickets.filter(tk => ticketMatchesStatusFilter(tk, 'resolved')).length,
    my_queue: enhancedTickets.filter(tk => ticketMatchesStatusFilter(tk, 'my_queue')).length,
  };

  const filteredTickets = enhancedTickets.filter(ticket => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = !q ||
                         (ticket.customerName || '').toLowerCase().includes(q) ||
                         (ticket.ticket_id || '').toLowerCase().includes(q) ||
                         (ticket.category || '').toLowerCase().includes(q);
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    const matchesStatus = ticketMatchesStatusFilter(ticket, filterStatus);
    const matchesAssignee = filterAssignee === 'all' || ticket.assignedId === filterAssignee;
    return matchesSearch && matchesPriority && matchesStatus && matchesAssignee;
  }).sort((a, b) => {
    const ta = new Date(a.created_at || a.createdAt || 0).getTime();
    const tb = new Date(b.created_at || b.createdAt || 0).getTime();
    return sortNewestFirst ? tb - ta : ta - tb;
  });

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('nav.ticketManagement')}</h1>
            <p className="text-gray-600">{t('ops.tm.subtitle')}</p>
          </div>
          <div className="flex space-x-3">
            <Dialog open={slaRulesOpen} onOpenChange={setSlaRulesOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Settings className="h-4 w-4 mr-2" />
                  {t('fin.tm.slaRules')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('fin.tm.slaRulesMgmt')}</DialogTitle>
                  <DialogDescription>
                    {t('fin.tm.slaRulesMgmtDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Add New SLA Rule */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg">
                        <Settings className="h-5 w-5 mr-2" />
                        {t('fin.tm.addSlaRule')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.ruleName')}</label>
                          <Input
                            placeholder={t('fin.tm.ruleNamePh1')}
                            value={newSlaRule.name}
                            onChange={(e) => setNewSlaRule(prev => ({ ...prev, name: e.target.value }))}
                          />
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.priorityLevel')}</label>
                          <Select value={newSlaRule.priority} onValueChange={(value) => setNewSlaRule(prev => ({ ...prev, priority: value }))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">{t('fin.opt.low')}</SelectItem>
                              <SelectItem value="medium">{t('fin.opt.medium')}</SelectItem>
                              <SelectItem value="high">{t('fin.opt.high')}</SelectItem>
                              <SelectItem value="critical">{t('fin.opt.critical')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium">{t('fin.tm.condition')}</label>
                        <Input
                          placeholder={t('fin.tm.conditionPh')}
                          value={newSlaRule.condition}
                          onChange={(e) => setNewSlaRule(prev => ({ ...prev, condition: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">{t('fin.tm.action')}</label>
                        <Textarea
                          placeholder={t('fin.tm.actionPh')}
                          value={newSlaRule.action}
                          onChange={(e) => setNewSlaRule(prev => ({ ...prev, action: e.target.value }))}
                          rows={2}
                        />
                      </div>
                      <Button onClick={handleAddSlaRule} className="w-full">
                        {t('fin.tm.addSlaRuleBtn')}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Existing SLA Rules */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium">{t('fin.tm.activeSlaRules')}</h3>
                    {slaRules.map((rule) => (
                      <Card key={rule.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <h4 className="font-medium">{rule.name}</h4>
                                <Badge variant={rule.active ? 'default' : 'secondary'}>
                                  {rule.active ? t('fin.tm.active') : t('fin.tm.inactive')}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                <strong>{t('fin.tm.when')}</strong> {rule.condition}
                              </p>
                              <p className="text-sm text-gray-600">
                                <strong>{t('fin.tm.then')}</strong> {rule.action}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleSlaRule(rule.id)}
                            >
                              {rule.active ? t('fin.tm.disable') : t('fin.tm.enable')}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={automationOpen} onOpenChange={setAutomationOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Zap className="h-4 w-4 mr-2" />
                  {t('fin.tm.automation')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('fin.tm.automationRules')}</DialogTitle>
                  <DialogDescription>
                    {t('fin.tm.automationRulesDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-6">
                  {/* Add New Automation Rule */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center text-lg">
                        <Zap className="h-5 w-5 mr-2" />
                        {t('fin.tm.addAutomationRule')}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <label className="text-sm font-medium">{t('fin.tm.ruleName')}</label>
                        <Input
                          placeholder={t('fin.tm.autoRuleNamePh')}
                          value={newAutomationRule.name}
                          onChange={(e) => setNewAutomationRule(prev => ({ ...prev, name: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">{t('fin.tm.triggerCondition')}</label>
                        <Input
                          placeholder={t('fin.tm.triggerConditionPh')}
                          value={newAutomationRule.trigger}
                          onChange={(e) => setNewAutomationRule(prev => ({ ...prev, trigger: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium">{t('fin.tm.automatedAction')}</label>
                        <Textarea
                          placeholder={t('fin.tm.automatedActionPh')}
                          value={newAutomationRule.action}
                          onChange={(e) => setNewAutomationRule(prev => ({ ...prev, action: e.target.value }))}
                          rows={2}
                        />
                      </div>
                      <Button onClick={handleAddAutomationRule} className="w-full">
                        {t('fin.tm.addAutomationRuleBtn')}
                      </Button>
                    </CardContent>
                  </Card>

                  {/* Existing Automation Rules */}
                  <div className="space-y-3">
                    <h3 className="text-lg font-medium">{t('fin.tm.activeAutomationRules')}</h3>
                    {automationRules.map((rule) => (
                      <Card key={rule.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center space-x-3">
                                <h4 className="font-medium">{rule.name}</h4>
                                <Badge variant={rule.active ? 'default' : 'secondary'}>
                                  {rule.active ? t('fin.tm.active') : t('fin.tm.inactive')}
                                </Badge>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                <strong>{t('fin.tm.trigger')}</strong> {rule.trigger}
                              </p>
                              <p className="text-sm text-gray-600">
                                <strong>{t('fin.tm.actionLabel')}</strong> {rule.action}
                              </p>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleAutomationRule(rule.id)}
                            >
                              {rule.active ? t('fin.tm.disable') : t('fin.tm.enable')}
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={reportsOpen} onOpenChange={setReportsOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
                  <BarChart3 className="h-4 w-4 mr-2" />
                  {t('fin.tm.reports')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{t('fin.tm.reportsTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('fin.tm.reportsDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => {
                        handleGenerateReports('SLA Performance');
                        setReportsOpen(false);
                      }}
                    >
                      <Clock className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('fin.tm.rptSlaPerf')}</span>
                      <span className="text-xs text-gray-500">{t('fin.tm.rptSlaPerfSub')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => {
                        handleGenerateReports('Agent Performance');
                        setReportsOpen(false);
                      }}
                    >
                      <Users className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('fin.tm.rptAgentPerf')}</span>
                      <span className="text-xs text-gray-500">{t('fin.tm.rptAgentPerfSub')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => {
                        handleGenerateReports('Automation Efficiency');
                        setReportsOpen(false);
                      }}
                    >
                      <Zap className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('fin.tm.rptAutoEff')}</span>
                      <span className="text-xs text-gray-500">{t('fin.tm.rptAutoEffSub')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => {
                        handleGenerateReports('Customer Satisfaction');
                        setReportsOpen(false);
                      }}
                    >
                      <BarChart3 className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('fin.tm.rptCsat')}</span>
                      <span className="text-xs text-gray-500">{t('fin.tm.rptCsatSub')}</span>
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Ticket Overview Stats */}
        <div className="grid md:grid-cols-5 gap-6 mb-8">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('fin.tm.totalTickets')}</p>
                  <p className="text-2xl font-bold text-gray-900">{enhancedTickets.length}</p>
                </div>
                <Ticket className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('fin.tm.highPriority')}</p>
                  <p className="text-2xl font-bold text-red-600">
                    {enhancedTickets.filter(t => t.priority === 'high').length}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('fin.tm.slaAtRisk')}</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {enhancedTickets.filter(t => t.slaStatus === 'due_soon' || t.slaStatus === 'breached').length}
                  </p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('fin.tm.unassigned')}</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {enhancedTickets.filter(t => !t.assignedId && t.status !== 'resolved' && t.status !== 'closed').length}
                  </p>
                </div>
                <User className="h-8 w-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{t('fin.tm.resolvedToday')}</p>
                  <p className="text-2xl font-bold text-green-600">
                    {enhancedTickets.filter(t => t.status === 'resolved').length}
                  </p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-64">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input
                  placeholder={t('fin.tm.searchPh')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={filterPriority} onValueChange={setFilterPriority}>
                <SelectTrigger className="w-32">
                  <SelectValue placeholder={t('fin.tm.priority')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('fin.tm.allPriority')}</SelectItem>
                  <SelectItem value="high">{t('fin.opt.high')}</SelectItem>
                  <SelectItem value="medium">{t('fin.opt.medium')}</SelectItem>
                  <SelectItem value="low">{t('fin.opt.low')}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder={t('fin.tm.status')} />
                </SelectTrigger>
                <SelectContent>
                  {/* GOV-7 WS1 Area E — canonical ticket.status filters (All/Active/Pending/Resolved/My Queue).
                      The prior options ('receiving_attention','more_details_required') are ESCALATION states,
                      not ticket states, so they matched nothing. Counts come from the same list authority. */}
                  <SelectItem value="all">{t('fin.tm.allStatus')} ({statusCounts.all})</SelectItem>
                  <SelectItem value="active">{t('fin.tm.active')} ({statusCounts.active})</SelectItem>
                  <SelectItem value="pending">{t('fin.tm.pending')} ({statusCounts.pending})</SelectItem>
                  <SelectItem value="resolved">{t('fin.tm.resolved')} ({statusCounts.resolved})</SelectItem>
                  <SelectItem value="my_queue">{t('fin.tm.myQueue')} ({statusCounts.my_queue})</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterAssignee} onValueChange={setFilterAssignee}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder={t('fin.tm.assignee')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('fin.tm.allAssignees')}</SelectItem>
                  {operators.map((operator) => (
                    <SelectItem key={operator.id} value={operator.id}>
                      {operator.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={loadTicketsAndConversations} title={t('fin.tm.applyTitle')}>
                <Filter className="h-4 w-4 mr-2" />
                {t('fin.tm.apply')}
              </Button>
              <Button variant="outline" size="sm" onClick={loadTicketsAndConversations}>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('common.refresh')}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Tickets List */}
        <div className="lg:col-span-2">
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{t('fin.tm.ticketsCount', { count: filteredTickets.length })}</span>
                <Button variant="outline" size="sm" onClick={() => setSortNewestFirst((v) => !v)} title={t('fin.tm.sortTitle')}>
                  <ArrowUpDown className="h-4 w-4 mr-2" />
                  {sortNewestFirst ? t('fin.tm.newest') : t('fin.tm.oldest')}
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loadError && (
                <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
                  {t('fin.tm.loadErr')} {loadError}.{' '}
                  <button className="underline font-medium" onClick={loadTicketsAndConversations}>{t('common.retry')}</button>
                </div>
              )}
              <div className="space-y-4 max-h-96 overflow-y-auto">
                {!loadError && filteredTickets.length === 0 && (
                  <div className="text-sm text-gray-500 p-4 text-center">{t('fin.tm.noMatch')}</div>
                )}
                {filteredTickets.map((ticket) => (
                  <div
                    key={ticket.id}
                    className={`p-4 rounded-lg cursor-pointer transition-all duration-200 border ${
                      selectedTicket?.id === ticket.id
                        ? 'bg-gradient-to-r from-blue-50 to-purple-50 border-purple-300'
                        : 'bg-white hover:bg-gray-50 border-gray-200'
                    }`}
                    onClick={() => setSelectedTicket(ticket)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          {getSourceIcon(ticket.source)}
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="bg-gradient-to-r from-blue-400 to-purple-500 text-white text-xs font-semibold">
                              {ticket.customerName?.charAt(0) || 'C'}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                        <div>
                          <h4 className="font-medium text-gray-900">{ticket.customerName}</h4>
                          <p className="text-sm text-gray-600">{ticket.category}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getSlaStatusColor(ticket.slaStatus)}>
                          {ticket.timeRemaining}
                        </Badge>
                        <div className="text-xs text-gray-500 mt-1">
                          {ticket.timestamp ? formatInTenantTz(ticket.timestamp, { dateStyle: 'medium' }) : 'N/A'}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-2">
                      <div className="flex space-x-2">
                        <Badge className={getPriorityColor(ticket.priority)}>
                          {ticket.priority}
                        </Badge>
                        <Badge variant="outline">
                          {ticket.status?.replace('_', ' ')}
                        </Badge>
                        {/* GOV-7 WS1 Area E — "escalated" is a SEPARATE dimension from lifecycle status
                            (a ticket may be open AND escalated), shown as its own badge, never a status. */}
                        {ticket.escalated && (
                          <Badge className="bg-orange-100 text-orange-800">{t('fin.tm.escalatedBadge')}</Badge>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">
                        {t('fin.tm.assignedTo')} {ticket.assignedTo}
                      </div>
                    </div>

                    <p className="text-sm text-gray-700 line-clamp-2 mb-2">
                      {ticket.lastMessage}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex flex-wrap gap-1">
                        {ticket.tags?.slice(0, 3).map((tag, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex space-x-1">
                        {ticket.automationTriggers?.map((trigger, index) => (
                          <Zap key={index} className="h-3 w-3 text-blue-500" title={trigger} />
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Ticket Details Panel */}
        <div className="lg:col-span-1">
          {selectedTicket ? (
            <div className="space-y-6">
              {/* M2 — READ-ONLY account-deletion governance review (Z8/Z13). Only for account_deletion
                  tickets. No approve/execute controls here; resolution is NOT erasure; executor stays OFF. */}
              {(selectedTicket.source === 'account_deletion' || selectedTicket.category === 'account_deletion') && (
                <Card className="bg-white/70 backdrop-blur-sm shadow-lg border-0 border-l-4 border-l-red-500">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      Account Deletion — Governance Review
                    </CardTitle>
                    <p className="text-xs text-gray-500 mt-1">
                      Read-only. Operators receive, communicate and refer only. Authorized manager/admin review only.
                      Resolving this ticket does NOT delete the account or any record.
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3 text-sm">
                    {deletionProjectionLoading && <div className="text-gray-500">Loading governance projection…</div>}
                    {deletionProjectionError === 'not_authorized' && (
                      <div className="text-amber-700">Policy review is restricted to tenant managers/admins.</div>
                    )}
                    {deletionProjectionError && deletionProjectionError !== 'not_authorized' && (
                      <div className="text-red-600">Could not load the governance projection ({deletionProjectionError}). Erasure remains blocked (fail-closed).</div>
                    )}
                    {deletionProjection && (
                      <>
                        {deletionProjection.legal_policy?.provenance_synthetic && (
                          <div className="rounded-md bg-amber-50 border border-amber-300 p-2 text-amber-800 text-xs">
                            ⚠ The assigned legal policy’s provenance is a staging/synthetic fixture. It does NOT provide
                            real-world legal or medical-law compliance. Do not rely on it for a real clinical-record decision.
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                          <span className="text-gray-500">Request scope</span>
                          <span className="font-medium">{deletionProjection.account_deletion_scope || '—'}</span>
                          <span className="text-gray-500">Industry profile</span>
                          <span className="font-medium">{deletionProjection.industry_profile?.profile_key || '—'}{deletionProjection.industry_profile?.profile_version != null ? ` v${deletionProjection.industry_profile.profile_version}` : ''}</span>
                          <span className="text-gray-500">Legal policy</span>
                          <span className="font-medium">{deletionProjection.legal_policy?.assigned ? `${deletionProjection.legal_policy.policy_key} v${deletionProjection.legal_policy.version}` : 'NOT ASSIGNED'}</span>
                          <span className="text-gray-500">Policy status</span>
                          <span className="font-medium">{deletionProjection.legal_policy?.lifecycle || '—'} / {deletionProjection.legal_policy?.review_status || '—'}{deletionProjection.legal_policy?.applicability_confirmed ? ' / applicability confirmed' : ''}</span>
                          <span className="text-gray-500">Jurisdiction</span>
                          <span className="font-medium">{[deletionProjection.legal_policy?.jurisdiction?.country, deletionProjection.legal_policy?.jurisdiction?.region, deletionProjection.legal_policy?.jurisdiction?.subregion].filter(Boolean).join(' / ') || '—'}</span>
                          <span className="text-gray-500">Legal hold</span>
                          <span className="font-medium">{deletionProjection.legal_hold?.status || '—'}</span>
                        </div>

                        <div>
                          <h4 className="font-medium mb-1 mt-2">Record categories (Z8 class × Z13 value)</h4>
                          <div className="space-y-2">
                            {(deletionProjection.retention_classes || []).length === 0 && (
                              <div className="text-gray-500">No retention classes declared by the pinned profile.</div>
                            )}
                            {(deletionProjection.retention_classes || []).map((r) => (
                              <div key={r.retention_class} className="rounded border border-gray-200 p-2">
                                <div className="flex justify-between items-center">
                                  <span className="font-medium">{r.retention_class}</span>
                                  <Badge className={
                                    r.decision === 'RESOLVED' ? 'bg-green-100 text-green-800'
                                      : r.decision === 'UNRESOLVED_FAIL_CLOSED' ? 'bg-red-100 text-red-800'
                                        : 'bg-gray-100 text-gray-700'}>
                                    {r.decision === 'UNRESOLVED_FAIL_CLOSED' ? 'UNRESOLVED — erasure blocked' : r.decision}
                                  </Badge>
                                </div>
                                <div className="text-xs text-gray-600 mt-1">
                                  Z8: {r.z8_applicability}{r.z8_declared_action ? ` · action ${r.z8_declared_action}` : ''} · Z13: {r.z13_value != null ? `${r.z13_value}${r.z13_action ? ` (${r.z13_action})` : ''}` : 'no value'}
                                </div>
                                {Array.isArray(r.z13_provenance) && r.z13_provenance.length > 0 && (
                                  <div className="text-[11px] text-gray-500 mt-1">
                                    Source: {r.z13_provenance.map((s) => `${s.authority_name || s.title || s.source_id}${s.authority_type ? ` [${s.authority_type}]` : ''}`).join('; ')}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center justify-between pt-1">
                          <span className="text-gray-500">Erasure decision</span>
                          <Badge className={deletionProjection.erasure_overall === 'BLOCKED_FAIL_CLOSED' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'}>
                            {deletionProjection.erasure_overall === 'BLOCKED_FAIL_CLOSED' ? 'BLOCKED (fail-closed)' : 'Eligible — subject to hold & retention action'}
                          </Badge>
                        </div>
                        <p className="text-[11px] text-gray-500">{deletionProjection.enforcement_note}</p>
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{t('fin.tm.ticketDetails')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">{t('fin.tm.customerInfo')}</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>{t('fin.tm.name')}</span>
                        <span className="font-medium">{selectedTicket.customerName}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('fin.tm.tier')}</span>
                        {/* Slice D — honest: real tenant-configured tier only, else "Not configured".
                            NO Stripe tier reuse, no invented Gold/Silver/VIP. */}
                        {customerContext?.tier_configured && customerContext?.tier ? (
                          <Badge className="bg-blue-100 text-blue-800">{customerContext.tier}</Badge>
                        ) : (
                          <span className="text-gray-500">{customerContextLoading ? '…' : t('fin.tm.notConfigured')}</span>
                        )}
                      </div>
                      <div className="flex justify-between">
                        <span>{t('fin.tm.previousTickets')}</span>
                        <span>{customerContextLoading ? '…' : (typeof customerContext?.previous_tickets === 'number' ? customerContext.previous_tickets : '—')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('fin.tm.totalTicketsLbl')}</span>
                        <span>{customerContextLoading ? '…' : (typeof customerContext?.total_tickets === 'number' ? customerContext.total_tickets : '—')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('fin.tm.satisfaction')}</span>
                        <span>
                          {customerContextLoading
                            ? '…'
                            : (customerContext && customerContext.satisfaction_score != null
                                ? `${customerContext.satisfaction_score}/5.0 (${customerContext.satisfaction_count})`
                                : '—')}
                        </span>
                      </div>
                      {customerContext?.last_ticket_at && (
                        <div className="flex justify-between">
                          <span>{t('fin.tm.lastTicket')}</span>
                          <span>{formatInTenantTz(customerContext.last_ticket_at, { dateStyle: 'medium' })}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">{t('fin.tm.slaManagement')}</h4>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm">{t('fin.tm.statusLbl')}</span>
                        <Badge className={getSlaStatusColor(selectedTicket.slaStatus)}>
                          {slaStatusLabel(selectedTicket.slaStatus)}
                        </Badge>
                      </div>
                      {selectedTicket.slaConfigured ? (
                        <div className="space-y-1">
                          <div className="flex justify-between text-sm">
                            <span>{t('fin.tm.timeRemaining')}</span>
                            <span className="font-medium">{selectedTicket.timeRemaining}</span>
                          </div>
                          {selectedTicket.slaDeadline && selectedTicket.created_at ? (
                            <Progress
                              value={(() => {
                                const start = new Date(selectedTicket.created_at).getTime();
                                const end = new Date(selectedTicket.slaDeadline).getTime();
                                if (!(end > start)) return 0;
                                const pct = ((Date.now() - start) / (end - start)) * 100;
                                return Math.max(0, Math.min(100, Math.round(pct)));
                              })()}
                              className="h-2"
                            />
                          ) : null}
                          {selectedTicket.slaDeadline && (
                            <div className="text-xs text-gray-500">
                              {t('fin.tm.deadlineLbl')} {new Date(selectedTicket.slaDeadline).toLocaleString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        // Honest: no SLA rule matches this ticket and no deadline is stored.
                        <div className="text-xs text-gray-500">
                          {t('fin.tm.noSlaConfigured')}
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={openExtendSla}
                      >
                        {t('fin.tm.extendSla')}
                      </Button>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">{t('fin.tm.assignment')}</h4>
                    {/* Current assignee shown as a NAME (never a raw id); "Unassigned" when none. */}
                    <div className="flex justify-between text-sm mb-2">
                      <span>{t('fin.tm.assignedTo')}</span>
                      <span className="font-medium">{selectedTicket.assignedTo || unassignedLabel}</span>
                    </div>
                    <Select
                      value={selectedTicket.assignedId || undefined}
                      onValueChange={(value) => handleTicketAssignment(selectedTicket.id, value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={unassignedLabel} />
                      </SelectTrigger>
                      <SelectContent>
                        {eligibleAssignees.length > 0
                          ? eligibleAssignees.map((s) => (
                              <SelectItem key={s.id} value={s.id}>
                                {s.name} — {roleLabel(s.role)}
                              </SelectItem>
                            ))
                          : <SelectItem value="no-staff" disabled>{t('fin.tm.noManagers')}</SelectItem>
                        }
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{t('fin.tm.escalationHistory')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedTicket.escalationHistory?.map((escalation, index) => (
                      <div key={index} className="flex items-center space-x-3 p-2 bg-gray-50 rounded">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <div className="flex-1">
                          <div className="text-sm font-medium">{t('fin.tm.level')} {escalation.level}</div>
                          <div className="text-xs text-gray-600">{escalation.reason}</div>
                        </div>
                        <div className="text-xs text-gray-500">
                          {formatInTenantTz(escalation.time)}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{t('fin.common.quickActions')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Dialog open={escalateDialogOpen} onOpenChange={setEscalateDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700">
                        {/* Correction 4 — flow assigns to a chosen operator; the label is the truthful "Escalate". */}
                        {t('fin.tm.escalate')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('fin.tm.escalateTicket')}</DialogTitle>
                        <DialogDescription>
                          {t('fin.tm.escalateDesc', { id: selectedTicket?.id })}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          {/* GOV-7 WS1 Area C — assign the escalation to an available (active, tenant-scoped)
                              operator. The chosen operator's staff id is sent as the canonical assigned_to. */}
                          <label className="text-sm font-medium">{t('fin.tm.selectOperator')}</label>
                          <Select value={escalateData.manager} onValueChange={(value) => setEscalateData(prev => ({...prev, manager: value}))}>
                            <SelectTrigger>
                              <SelectValue placeholder={t('fin.tm.selectOperatorPh')} />
                            </SelectTrigger>
                            <SelectContent>
                              {availableOperators.length > 0
                                ? availableOperators.map(s => (
                                    <SelectItem key={s.id} value={s.id}>
                                      {s.name || s.email} — {roleLabel(s.role)}
                                    </SelectItem>
                                  ))
                                : <SelectItem value="no-staff" disabled>{t('fin.tm.noOperators')}</SelectItem>
                              }
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.urgencyLevel')}</label>
                          <Select value={escalateData.urgency} onValueChange={(value) => setEscalateData(prev => ({...prev, urgency: value}))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">{t('fin.tm.urgLow')}</SelectItem>
                              <SelectItem value="normal">{t('fin.tm.urgNormal')}</SelectItem>
                              <SelectItem value="high">{t('fin.tm.urgHigh')}</SelectItem>
                              <SelectItem value="critical">{t('fin.tm.urgCritical')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.escalationReason')}</label>
                          <Textarea
                            placeholder={t('fin.tm.escalationReasonPh')}
                            value={escalateData.reason}
                            onChange={(e) => setEscalateData(prev => ({...prev, reason: e.target.value}))}
                            rows={3}
                          />
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setEscalateDialogOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={handleEscalateToManager}>
                            {t('fin.tm.escalateTicket')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full">
                        {t('fin.tm.addInternalNote')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('fin.tm.addInternalNote')}</DialogTitle>
                        <DialogDescription>
                          {t('fin.tm.addNoteDesc', { id: selectedTicket?.id })}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.noteVisibility')}</label>
                          <Select value={noteData.visibility} onValueChange={(value) => setNoteData(prev => ({...prev, visibility: value}))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="internal">{t('fin.tm.visInternal')}</SelectItem>
                              <SelectItem value="customer">{t('fin.tm.visCustomer')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.noteContent')}</label>
                          <Textarea
                            placeholder={t('fin.tm.noteContentPh')}
                            value={noteData.note}
                            onChange={(e) => setNoteData(prev => ({...prev, note: e.target.value}))}
                            rows={4}
                          />
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={handleAddInternalNote}>
                            {t('fin.tm.addNote')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  <Dialog open={priorityDialogOpen} onOpenChange={setPriorityDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full">
                        {t('fin.tm.setPriority')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('fin.tm.setTicketPriority')}</DialogTitle>
                        <DialogDescription>
                          {t('fin.tm.setPriorityDesc', { id: selectedTicket?.id })}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.priorityLevel')}</label>
                          <Select value={priorityData.priority} onValueChange={(value) => setPriorityData(prev => ({...prev, priority: value}))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="low">{t('fin.tm.prioLow')}</SelectItem>
                              <SelectItem value="medium">{t('fin.tm.prioMedium')}</SelectItem>
                              <SelectItem value="high">{t('fin.tm.prioHigh')}</SelectItem>
                              <SelectItem value="urgent">{t('fin.tm.prioUrgent')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.reasonForChange')}</label>
                          <Textarea
                            placeholder={t('fin.tm.reasonForChangePh')}
                            value={priorityData.reason}
                            onChange={(e) => setPriorityData(prev => ({...prev, reason: e.target.value}))}
                            rows={2}
                          />
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setPriorityDialogOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={handleSetPriority}>
                            {t('fin.tm.updatePriority')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Reopen button — only shown for resolved/closed tickets */}
                  {selectedTicket && ['resolved', 'closed'].includes(selectedTicket.status) && (
                    <Button
                      variant="outline"
                      className="w-full border-orange-300 text-orange-700 hover:bg-orange-50"
                      onClick={() => {
                        const reason = window.prompt(t('fin.tm.reopenPrompt')) || '';
                        handleReopenTicket(reason);
                      }}
                    >
                      {t('fin.tm.reopenTicket')}
                    </Button>
                  )}

                  <Dialog open={resolveDialogOpen} onOpenChange={setResolveDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" className="w-full">
                        {t('fin.tm.markResolved')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-lg">
                      <DialogHeader>
                        <DialogTitle>{t('fin.tm.resolveTicket')}</DialogTitle>
                        <DialogDescription>
                          {t('fin.tm.resolveDesc', { id: selectedTicket?.id })}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.resolutionSummary')}</label>
                          <Textarea
                            placeholder={t('fin.tm.resolutionSummaryPh')}
                            value={resolveData.resolution}
                            onChange={(e) => setResolveData(prev => ({...prev, resolution: e.target.value}))}
                            rows={4}
                          />
                        </div>
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="customerNotified"
                            checked={resolveData.customerNotified}
                            onChange={(e) => setResolveData(prev => ({...prev, customerNotified: e.target.checked}))}
                          />
                          <label htmlFor="customerNotified" className="text-sm font-medium">
                            {t('fin.tm.notifyCustomer')}
                          </label>
                        </div>
                        {/* GOV-7 WS1 Area B / Correction 2 — MANDATORY governed knowledge. Meaningful Resolution
                            Steps are REQUIRED to resolve an operator-resolved support ticket; the reusable
                            candidate (source=human_resolution) is created server-side BEFORE the ticket is
                            finalized. There is NO opt-out. */}
                        <div className="rounded-lg border border-gray-200 p-3 space-y-3">
                          <p className="text-sm font-medium">{t('fin.tm.resolutionStepsGovTitle')}</p>
                          <div>
                            <label className="text-xs font-medium text-gray-600">Title</label>
                            <Input
                              placeholder="Short problem title"
                              value={resolveData.solutionTitle}
                              onChange={(e) => setResolveData(prev => ({...prev, solutionTitle: e.target.value}))}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-600">{t('fin.tm.resolutionStepsLabel')}</label>
                            <Textarea
                              placeholder="Describe the exact steps that resolved the issue (one per line)"
                              value={resolveData.resolutionSteps}
                              onChange={(e) => setResolveData(prev => ({...prev, resolutionSteps: e.target.value}))}
                              rows={4}
                              className="mt-1"
                            />
                          </div>
                          <p className="text-xs text-gray-500">{t('fin.tm.knowledgeGovernanceHint')}</p>
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button variant="outline" onClick={() => setResolveDialogOpen(false)}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={handleMarkAsResolved}>
                            {t('fin.tm.markResolved')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Slice C — Extend SLA modal (controlled; opened by the Extend SLA button above) */}
                  <Dialog open={extendOpen} onOpenChange={setExtendOpen}>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>{t('fin.tm.extendSla')}</DialogTitle>
                        <DialogDescription>
                          {t('fin.tm.extendSlaDesc', { id: selectedTicket?.id })}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="flex space-x-2">
                          <div className="flex-1">
                            <label className="text-sm font-medium">{t('fin.tm.duration')}</label>
                            <Input
                              type="number"
                              min="1"
                              placeholder={t('fin.tm.durationPh')}
                              value={extendData.amount}
                              onChange={(e) => setExtendData(prev => ({ ...prev, amount: e.target.value }))}
                            />
                          </div>
                          <div className="w-32">
                            <label className="text-sm font-medium">{t('fin.tm.unit')}</label>
                            <Select value={extendData.unit} onValueChange={(v) => setExtendData(prev => ({ ...prev, unit: v }))}>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="hours">{t('fin.tm.hours')}</SelectItem>
                                <SelectItem value="minutes">{t('fin.tm.minutes')}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div>
                          <label className="text-sm font-medium">{t('fin.tm.reason')}</label>
                          <Textarea
                            placeholder={t('fin.tm.reasonPh')}
                            value={extendData.reason}
                            onChange={(e) => setExtendData(prev => ({ ...prev, reason: e.target.value }))}
                            rows={3}
                          />
                        </div>
                        <div className="flex justify-end space-x-2 pt-2">
                          <Button variant="outline" onClick={() => setExtendOpen(false)} disabled={extendSubmitting}>
                            {t('common.cancel')}
                          </Button>
                          <Button onClick={submitExtendSla} disabled={extendSubmitting}>
                            {extendSubmitting ? t('fin.tm.extending') : t('fin.tm.extendSla')}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>

              {/* Quick Response */}
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">{t('fin.tm.quickResponse')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Textarea
                    placeholder={t('fin.tm.quickResponsePh')}
                    value={quickResponseText}
                    onChange={(e) => setQuickResponseText(e.target.value)}
                    rows={3}
                  />
                  <Button
                    className="w-full"
                    onClick={() => handleQuickResponse(quickResponseText)}
                    disabled={!quickResponseText.trim()}
                  >
                    <MessageSquare className="h-4 w-4 mr-2" />
                    {t('fin.tm.sendToCustomer')}
                  </Button>
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardContent className="p-12 text-center">
                <Ticket className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">{t('fin.tm.selectTicket')}</h3>
                <p className="text-gray-500">{t('fin.tm.selectTicketDesc')}</p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketManagement;